import { useMemo, useState } from "react";
import { Pager } from "./ui";

/*
 * Client-side table with search, sorting and paging (replaces the jQuery DataTables the PHP pages used).
 * columns: [{ key, label, num?, sortable? (default true), render?(row), sortValue?(row) }]
 */
export default function DataTable({ columns, rows, rowKey, searchPlaceholder = "Search", initialSort, pageSizes = [10, 25, 50, 100],
  empty = "No records found.", toolbarExtra, footer }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(initialSort || { key: columns[0].key, dir: 1 });
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSizes[0]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term ? rows.filter((r) => columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(term))) : rows;
    const col = columns.find((c) => c.key === sort.key);
    const val = (r) => (col?.sortValue ? col.sortValue(r) : r[sort.key]);
    return [...list].sort((a, b) => {
      const x = val(a) ?? "", y = val(b) ?? "";
      return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true })) * sort.dir;
    });
  }, [rows, columns, q, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const cur = Math.min(page, pages);
  const shown = filtered.slice((cur - 1) * size, cur * size);

  return (
    <>
      <div className="toolbar">
        <div className="field grow"><div className="search"><i className="fas fa-magnifying-glass" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder={searchPlaceholder} aria-label={searchPlaceholder} /></div></div>
        {toolbarExtra}
        <div className="field" style={{ minWidth: "7rem" }}><label htmlFor="dt-size" className="visually-hidden">Rows per page</label>
          <select id="dt-size" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}>
            {pageSizes.map((n) => <option key={n} value={n}>{n} rows</option>)}
          </select></div>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead><tr>
            {columns.map((c) => {
              const sortable = c.sortable !== false;
              const on = sort.key === c.key;
              return (
                <th key={c.key} className={`${sortable ? "sortable" : ""} ${c.num ? "num" : ""}`}
                  onClick={sortable ? () => { setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 })); setPage(1); } : undefined}
                  aria-sort={on ? (sort.dir === 1 ? "ascending" : "descending") : undefined}>
                  {c.label}{on && <> <i className={`fas ${sort.dir === 1 ? "fa-sort-up" : "fa-sort-down"}`} /></>}
                </th>
              );
            })}
          </tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={rowKey ? rowKey(r, i) : i}>
                {columns.map((c) => <td key={c.key} className={c.num ? "num mono" : ""}>{c.render ? c.render(r) : r[c.key]}</td>)}
              </tr>
            ))}
          </tbody>
          {footer}
        </table>
        {filtered.length === 0 && <div className="empty"><i className="fas fa-inbox" />{empty}</div>}
      </div>
      <div className="ui-pager" style={{ justifyContent: "space-between" }}>
        <span>Showing {filtered.length ? (cur - 1) * size + 1 : 0} to {Math.min(cur * size, filtered.length)} of {filtered.length}
          {filtered.length !== rows.length ? ` (filtered from ${rows.length})` : ""}</span>
        <Pager page={cur} totalPages={pages} onPage={setPage} />
      </div>
    </>
  );
}
